import { useState, useCallback, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  DollarSign, _Upload, Loader2, AlertTriangle, Search,
  RefreshCw, FileSpreadsheet, CheckCircle, _Clock, _XCircle, FileUp,
  Eye, FileText, ExternalLink, FileDown, Package
} from 'lucide-react';
import { fmtFecha } from '../lib/utils'; // Iter 292.g — formato DD-MM-YYYY

const fmt = (num) => Math.round(num || 0).toLocaleString('es-CL');

export default function CuentasPorPagarRCV() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterFuente, setFilterFuente] = useState('todos');
  const [uploading, setUploading] = useState(false);
  const [docModal, setDocModal] = useState({ open: false, loading: false, data: null });

  const handleViewDocument = useCallback(async (filename) => {
    setDocModal({ open: true, loading: true, data: null });
    try {
      const res = await api.get(`/sharepoint/buscar-documento?filename=${encodeURIComponent(filename)}`);
      setDocModal({ open: true, loading: false, data: res.data?.found ? res.data : { found: false, filename } });
    } catch {
      setDocModal({ open: true, loading: false, data: { found: false, filename } });
    }
  }, []);

  const loadData = useCallback(async (file = null) => {
    setLoading(true); setError(null);
    try {
      let res;
      if (file) {
        const formData = new FormData();
        formData.append('file', file);
        res = await api.post('/contabilidad/cuentas-pagar-rcv', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        res = await api.post('/contabilidad/cuentas-pagar-rcv');
      }
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cargar datos');
    } finally {
      setLoading(false); setUploading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    loadData(file);
    e.target.value = '';
  };

  const filteredFacturas = (data?.facturas || []).filter(f => {
    const matchSearch = !searchTerm ||
      f.proveedor_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.rut_proveedor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      f.numero?.toString().includes(searchTerm);
    const matchFuente = filterFuente === 'todos' ||
      (filterFuente === 'alegra' && f.fuente === 'alegra') ||
      (filterFuente === 'rcv' && f.fuente === 'rcv');
    return matchSearch && matchFuente;
  });

  const t = data?.totales || {};

  return (
    <div className="space-y-5" data-testid="cuentas-pagar-rcv-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-blue-600" />
          Cuentas por Pagar (Alegra + RCV)
        </h1>
        <p className="text-sm text-muted-foreground">
          Facturas pendientes de pago combinando Alegra y Registro de Compras y Ventas del SII.
        </p>
      </div>

      {/* Toolbar */}
      <div className="bg-white border rounded-lg p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px] max-w-sm">
            <label className="block text-xs font-medium mb-1 text-gray-600">Buscar</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input placeholder="Proveedor, RUT o N. factura..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="search-cxp-input" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Fuente</label>
            <div className="flex gap-1">
              {[
                { val: 'todos', label: 'Todos' },
                { val: 'alegra', label: 'Alegra' },
                { val: 'rcv', label: 'Solo RCV' },
              ].map(opt => (
                <button key={opt.val} onClick={() => setFilterFuente(opt.val)}
                  className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${filterFuente === opt.val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}
                  data-testid={`filter-fuente-${opt.val}`}>{opt.label}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Cargar RCV Compras</label>
            <label className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-md border text-sm cursor-pointer transition-colors ${uploading ? 'bg-gray-100 text-gray-400' : 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100'}`} data-testid="upload-rcv-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileUp className="w-4 h-4" />}
              {uploading ? 'Cargando...' : 'Subir CSV RCV'}
              <input type="file" accept=".csv,.txt" onChange={handleFileUpload} className="hidden" disabled={uploading} />
            </label>
          </div>
          <Button variant="outline" onClick={() => loadData()} disabled={loading} data-testid="refresh-cxp-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary */}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border rounded p-3 text-center">
              <p className="text-xs text-muted-foreground">Total Facturas</p>
              <p className="text-xl font-bold" data-testid="total-facturas-cxp">{t.total_facturas}</p>
              <p className="text-[10px] text-muted-foreground">Alegra: {t.total_alegra} / RCV: {t.total_rcv}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-center">
              <p className="text-xs text-blue-700">Monto Alegra</p>
              <p className="text-lg font-bold text-blue-700">${fmt(t.monto_alegra)}</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-center">
              <p className="text-xs text-amber-700">Monto RCV (no en Alegra)</p>
              <p className="text-lg font-bold text-amber-700">${fmt(t.monto_rcv)}</p>
            </div>
            <div className="bg-gray-900 rounded p-3 text-center">
              <p className="text-xs text-gray-400">Total Pendiente</p>
              <p className="text-lg font-bold text-white">${fmt(t.monto_total)}</p>
            </div>
          </div>

          {/* Overdue breakdown */}
          <div className="grid grid-cols-5 gap-2">
            <div className="bg-green-50 border border-green-200 rounded p-2 text-center">
              <p className="text-[10px] text-green-700">No vencidas</p>
              <p className="text-sm font-bold text-green-700">${fmt(t.no_vencidas)}</p>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded p-2 text-center">
              <p className="text-[10px] text-yellow-700">Vencidas 1-30d</p>
              <p className="text-sm font-bold text-yellow-700">${fmt(t.vencidas_30)}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded p-2 text-center">
              <p className="text-[10px] text-orange-700">Vencidas 31-60d</p>
              <p className="text-sm font-bold text-orange-700">${fmt(t.vencidas_60)}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded p-2 text-center">
              <p className="text-[10px] text-red-700">Vencidas 61-90d</p>
              <p className="text-sm font-bold text-red-700">${fmt(t.vencidas_90)}</p>
            </div>
            <div className="bg-red-100 border border-red-300 rounded p-2 text-center">
              <p className="text-[10px] text-red-800">Vencidas +90d</p>
              <p className="text-sm font-bold text-red-800">${fmt(t.vencidas_91plus)}</p>
            </div>
          </div>

          {data.rcv_loaded && data.rcv_count > 0 && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-800 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 shrink-0" />
              <span><strong>{data.rcv_count} facturas del RCV</strong> no estan registradas en Alegra. Se muestran como fuente "RCV".</span>
            </div>
          )}
          {data.rcv_loaded && data.rcv_count === 0 && (
            <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-sm text-green-800 flex items-center gap-2">
              <CheckCircle className="w-5 h-5 shrink-0" />
              <span>Todas las facturas del RCV estan registradas en Alegra.</span>
            </div>
          )}
          {!data.rcv_loaded && (
            <div className="bg-gray-50 border rounded-lg p-3 text-sm text-gray-600 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 shrink-0 text-gray-400" />
              <span>Suba un archivo CSV del RCV (Registro de Compras) del SII para ver facturas no registradas en Alegra.</span>
            </div>
          )}
        </>
      )}

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm"><AlertTriangle className="w-4 h-4 inline mr-1" /> {error}</div>}
      {loading && <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /><span className="ml-3 text-gray-600">Cargando cuentas por pagar...</span></div>}

      {/* Table */}
      {!loading && filteredFacturas.length > 0 && (
        <div className="border rounded-lg" style={{ overflowX: 'clip' }}>
            <table className="w-full text-xs" data-testid="cxp-table">
              <thead className="sticky top-14 lg:top-0 z-20">
                <tr className="bg-gray-800 text-white text-left">
                  <th className="px-3 py-2">Fuente</th>
                  <th className="px-3 py-2">N. Factura</th>
                  <th className="px-3 py-2">Proveedor</th>
                  <th className="px-3 py-2">RUT</th>
                  <th className="px-3 py-2 text-center">OC</th>
                  <th className="px-3 py-2 text-center">Recepción</th>
                  <th className="px-3 py-2">F. Emision</th>
                  <th className="px-3 py-2">F. Vencimiento</th>
                  <th className="px-3 py-2 text-right">Total</th>
                  <th className="px-3 py-2 text-right">Saldo</th>
                  <th className="px-3 py-2">Vencimiento</th>
                  <th className="px-3 py-2 text-center">Doc</th>
                </tr>
              </thead>
              <tbody>
                {filteredFacturas.map((f, i) => {
                  const isOverdue = f.dias_vencida > 0;
                  const docFilename = f.ruta_archivo ? f.ruta_archivo.split('\\').pop().split('/').pop() : null;
                  return (
                    <tr key={`${f.fuente}-${f.numero}-${i}`}
                      className={`border-t transition-colors ${f.fuente === 'rcv' ? 'bg-amber-50/50 hover:bg-amber-50' : 'hover:bg-gray-50'}`}
                      data-testid={`cxp-row-${f.numero}`}>
                      <td className="px-3 py-2">
                        {f.fuente === 'alegra' ? (
                          <span className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-medium">
                            <CheckCircle className="w-3 h-3" /> Alegra
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-medium">
                            <FileSpreadsheet className="w-3 h-3" /> RCV
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-blue-700">{f.numero}</td>
                      <td className="px-3 py-2 truncate max-w-[200px]" title={f.proveedor_nombre}>{f.proveedor_nombre}</td>
                      <td className="px-3 py-2 font-mono text-gray-500 text-[10px]">{f.rut_proveedor}</td>
                      <td className="px-3 py-2 text-center">
                        {f.estado_oc === 'Con OC' ? (
                          <span className="inline-flex items-center gap-0.5 bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-medium" data-testid={`oc-badge-rcv-${f.numero}`}>
                            <Package className="w-3 h-3" /> {f.numero_oc}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {f.estado_recepcion === 'Recepcionada' ? (
                          <span className="inline-flex items-center gap-0.5 bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-[10px] font-medium" data-testid={`rec-badge-rcv-${f.numero}`}>
                            <CheckCircle className="w-3 h-3" /> Si
                          </span>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{fmtFecha(f.fecha_emision) || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {f.fecha_vencimiento ? (
                          <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}>{f.fecha_vencimiento}</span>
                        ) : <span className="text-gray-300">-</span>}
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-medium">${fmt(f.total)}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        <span className={f.saldo > 0 ? 'text-red-700 font-medium' : 'text-green-700'}>${fmt(f.saldo)}</span>
                      </td>
                      <td className="px-3 py-2">
                        {f.categoria_vencimiento === 'no_vencida' && <span className="text-green-600 text-[10px]">Al dia</span>}
                        {f.categoria_vencimiento === 'vencida_30' && <span className="text-yellow-600 text-[10px]">1-30 dias</span>}
                        {f.categoria_vencimiento === 'vencida_60' && <span className="text-orange-600 text-[10px]">31-60 dias</span>}
                        {f.categoria_vencimiento === 'vencida_90' && <span className="text-red-600 text-[10px]">61-90 dias</span>}
                        {f.categoria_vencimiento === 'vencida_91plus' && <span className="text-red-800 font-medium text-[10px]">+90 dias</span>}
                        {f.categoria_vencimiento === 'sin_info' && <span className="text-gray-400 text-[10px]">Sin info</span>}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {docFilename ? (
                          <button onClick={() => handleViewDocument(docFilename)}
                            className="inline-flex items-center gap-0.5 text-blue-600 hover:text-blue-800 text-[10px] bg-blue-50 px-2 py-1 rounded"
                            data-testid={`doc-btn-rcv-${f.numero}`}>
                            <Eye className="w-3 h-3" />
                          </button>
                        ) : (
                          <span className="text-gray-300 text-[10px]">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 border-t font-medium text-xs">
                  <td colSpan={8} className="px-3 py-2 text-right text-gray-600">Total ({filteredFacturas.length} facturas):</td>
                  <td className="px-3 py-2 text-right font-mono font-bold">${fmt(filteredFacturas.reduce((s, f) => s + (f.total || 0), 0))}</td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-red-700">${fmt(filteredFacturas.reduce((s, f) => s + (f.saldo || 0), 0))}</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            </table>
        </div>
      )}

      {!loading && data && filteredFacturas.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-600 font-medium">No se encontraron facturas</p>
          <p className="text-sm text-muted-foreground mt-1">{searchTerm ? 'Intente con otro termino' : 'No hay cuentas por pagar pendientes'}</p>
        </div>
      )}

      {/* Document Viewer Modal */}
      <Dialog open={docModal.open} onOpenChange={(open) => !docModal.loading && setDocModal({ ...docModal, open })}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" /> {docModal.data?.filename || 'Documento'}
            </DialogTitle>
          </DialogHeader>
          {docModal.loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Buscando en SharePoint...</span>
            </div>
          )}
          {docModal.data && !docModal.data.found && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded p-4 text-yellow-800">
                <AlertTriangle className="w-5 h-5 inline mr-2" />
                Documento <strong>{docModal.data.filename}</strong> no encontrado en SharePoint.
              </div>
              <div className="text-center py-4">
                <FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-600 mb-4">El documento puede estar en la carpeta de SharePoint.</p>
                <Button onClick={() => window.open('https://chirinosteelcom.sharepoint.com/:f:/s/ServidorChsteel/IgCijncVJt1bRZy1oVHJFg41Ae24HqfThKmBpQ_OMKYpy2w?e=KUf46I', '_blank')}
                  className="flex items-center gap-2 mx-auto bg-blue-600 hover:bg-blue-700">
                  <ExternalLink className="w-4 h-4" /> Abrir Carpeta en SharePoint
                </Button>
              </div>
            </div>
          )}
          {docModal.data?.found && (
            <div className="space-y-4">
              {docModal.data.previewUrl ? (
                <div className="border rounded-lg overflow-hidden bg-gray-100" style={{ height: '500px' }}>
                  <iframe src={docModal.data.previewUrl} className="w-full h-full" title="Vista previa" />
                </div>
              ) : (
                <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                  <p>Vista previa no disponible</p>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                {docModal.data.webUrl && (
                  <Button variant="outline" onClick={() => window.open(docModal.data.webUrl, '_blank')}>
                    <ExternalLink className="w-4 h-4 mr-2" /> Abrir en SharePoint
                  </Button>
                )}
                <Button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sharepoint/descargar/${docModal.data.file_id}`, '_blank')}
                  className="bg-blue-600 hover:bg-blue-700">
                  <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
                </Button>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocModal({ open: false, loading: false, data: null })}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
